import React, { useState, useEffect } from 'react';
import { Play, Film } from 'lucide-react';
import axios from 'axios';
import ReelsPlayer from './ReelsPlayer';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ReelsSection = () => {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [selectedReelId, setSelectedReelId] = useState(null);

  useEffect(() => {
    fetchReels();
  }, []);

  const fetchReels = async () => {
    try {
      // Fetch exactly 5 reels for the homepage
      const response = await axios.get(`${API}/api/reels?limit=5`);
      setReels(response.data.reels || []);
    } catch (err) {
      console.error('Error fetching reels:', err);
    } finally {
      setLoading(false);
    }
  };

  const openReel = (reelId) => {
    setSelectedReelId(reelId);
    setPlayerOpen(true);
  };

  const formatViews = (count) => {
    if (!count) return '0 views';
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M views`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K views`;
    return `${count} views`;
  };

  if (loading) {
    return (
      <section className="py-6 border-t border-white/10" data-testid="reels-section-loading">
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-lg font-bold">Reels</h2>
        </div>
        <div className="flex gap-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="w-[160px] flex-shrink-0">
              <div className="aspect-[9/16] bg-gray-800 rounded-lg animate-pulse" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (reels.length === 0) {
    return null;
  }

  return (
    <>
      <section className="py-6 border-t border-white/10" data-testid="reels-section">
        {/* Reels Header */}
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-lg font-bold">Reels</h2>
        </div>

        {/* 5 Shorts in a Row - Smaller Cards */}
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
          {reels.slice(0, 5).map((reel) => (
            <div
              key={reel.id}
              className="w-[160px] sm:w-[180px] flex-shrink-0 cursor-pointer group"
              onClick={() => openReel(reel.id)}
              data-testid={`reel-card-${reel.id}`}
            >
              {/* Thumbnail Container - Smaller */}
              <div className="relative aspect-[9/16] rounded-lg overflow-hidden bg-gray-800 mb-2">
                {/* Thumbnail Image */}
                {reel.thumbnail_url ? (
                  <img
                    src={reel.thumbnail_url}
                    alt={reel.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-700 to-gray-800">
                    <Film className="h-8 w-8 text-gray-500" />
                  </div>
                )}

                {/* Gradient overlay at bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/80 to-transparent" />

                {/* Play button on hover */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="w-12 h-12 rounded-full bg-black/60 flex items-center justify-center">
                    <Play className="h-5 w-5 text-white fill-white ml-0.5" />
                  </div>
                </div>
              </div>

              {/* Title - 2 lines max */}
              <h3 className="text-sm font-medium text-white line-clamp-2 leading-snug mb-1">
                {reel.title}
              </h3>

              {/* View count */}
              <p className="text-xs text-gray-400">
                {formatViews(reel.view_count)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Reels Player Modal */}
      <ReelsPlayer
        isOpen={playerOpen}
        onClose={() => setPlayerOpen(false)}
        initialReelId={selectedReelId}
        reels={reels}
      />
    </>
  );
};

export default ReelsSection;
